// RIZ127 (mig 470) - First to Claim offers, in words. Pure: no React, no supabase-js, so
// `node --test` imports it directly (tests/unit/riz127-lead-groups.test.mjs).
//
// An offer is read through lead_claim_offers_for_me (the database's ALLOW-LIST: the lead's name,
// source, the property asked about, its town and price, the side; never the phone or email) and
// claimed through claim_lead_offer, which answers with an outcome. Everything a member is told
// about an offer is worded here, so the bell, the Contacts page and Leads to claim say the same.

/** One row of lead_claim_offers_for_me. Mirrors LeadOfferRow in src/lib/leadClaimDb.ts. */
export interface LeadOffer {
  offer_id: string
  /** open | claimed | expired | withdrawn - or, while still open: assigned (by hand) | closing (window over) */
  status: string
  offered_at: string
  expires_at: string
  claim_window_minutes: number
  lead_name: string | null
  lead_source_label: string | null
  inquiry_address: string | null
  inquiry_city: string | null
  inquiry_price: string | null
  lead_side: string | null
  claimed_by: string | null
  claimed_by_name: string | null
  claimed_at: string | null
  resolved_agent_id: string | null
  resolved_agent_name: string | null
  resolution: string | null
  contact_id: string | null
  can_claim: boolean
}

export type ClaimOutcome =
  | { outcome: 'claimed'; offer_id: string; contact_id: string }
  | { outcome: 'taken'; offer_id: string; claimed_by_name: string | null; contact_id: string | null }
  | { outcome: 'expired'; offer_id: string; resolved_agent_name: string | null }
  | { outcome: 'too_late'; offer_id: string }
  | { outcome: 'withdrawn'; offer_id: string }

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)

/** claim_lead_offer's answer, checked. Anything unexpected is null - never read as a win. */
export function readClaimOutcome(data: unknown): ClaimOutcome | null {
  if (!data || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  const offerId = str(d.offer_id)
  if (!offerId) return null
  switch (d.outcome) {
    case 'claimed': {
      const contactId = str(d.contact_id)
      return contactId ? { outcome: 'claimed', offer_id: offerId, contact_id: contactId } : null
    }
    case 'taken':
      return { outcome: 'taken', offer_id: offerId, claimed_by_name: str(d.claimed_by_name), contact_id: str(d.contact_id) }
    case 'expired':
      return { outcome: 'expired', offer_id: offerId, resolved_agent_name: str(d.resolved_agent_name) }
    case 'too_late':
      return { outcome: 'too_late', offer_id: offerId }
    case 'withdrawn':
      return { outcome: 'withdrawn', offer_id: offerId }
    default:
      return null
  }
}

/** What to tell the person who pressed Claim. */
export function claimOutcomeMessage(o: ClaimOutcome): string {
  switch (o.outcome) {
    case 'claimed':
      return 'It is yours.'
    case 'taken':
      // claimed_by_name is set whenever someone has it; the claimer's own earlier claim reads the same.
      return o.contact_id
        ? 'You already claimed this lead.'
        : `${o.claimed_by_name ?? 'Someone else'} got it first.`
    case 'expired':
      return o.resolved_agent_name
        ? `Nobody claimed it in time, so it went to ${o.resolved_agent_name}.`
        : 'Nobody claimed it in time.'
    case 'too_late':
      return 'The claim window has closed. It is being handed on now.'
    case 'withdrawn':
      return 'This lead is no longer available.'
  }
}

/** Whole minutes left to claim, never below zero. */
export function minutesLeft(o: Pick<LeadOffer, 'expires_at'>, nowMs: number): number {
  const ms = Date.parse(o.expires_at) - nowMs
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 60000)) : 0
}

/**
 * The one line that says where an offer stands, for the person reading it. "The others see it
 * was claimed and by whom" is this line: Claimed by <name>.
 */
export function offerStatusLine(o: LeadOffer, meId: string | null, nowMs: number): string {
  const mine = (id: string | null) => !!meId && id === meId
  switch (o.status) {
    case 'open': {
      if (!o.can_claim) return 'Up for grabs'
      const left = minutesLeft(o, nowMs)
      return left <= 1 ? 'Less than a minute left to claim' : `${left} minutes left to claim`
    }
    case 'closing':
      return 'Time is up. It is being handed on.'
    case 'claimed':
      return mine(o.claimed_by) ? 'You claimed it' : `Claimed by ${o.claimed_by_name ?? 'a teammate'}`
    case 'expired':
      if (mine(o.resolved_agent_id)) return 'Nobody claimed it in time, so it is yours'
      return o.resolved_agent_name
        ? `Nobody claimed it in time. It went to ${o.resolved_agent_name}`
        : 'Nobody claimed it in time. It is unassigned'
    case 'assigned':
    case 'withdrawn':
      if (o.resolved_agent_id) {
        return mine(o.resolved_agent_id) ? 'Assigned to you' : `Assigned to ${o.resolved_agent_name ?? 'a teammate'}`
      }
      return 'No longer available'
    default:
      return 'No longer available'
  }
}

const SIDE_WORD: Record<string, string> = { buyer: 'Buyer', seller: 'Seller', both: 'Buying and selling' }

/** "123 Main St · Rigby · $425,000 · Buyer · Zillow" - only what the offer carries. */
export function offerDetails(o: LeadOffer): string {
  const parts: string[] = []
  if (o.inquiry_address) parts.push(o.inquiry_address)
  if (o.inquiry_city && !(o.inquiry_address ?? '').includes(o.inquiry_city)) parts.push(o.inquiry_city)
  const price = Number(o.inquiry_price)
  if (o.inquiry_price && Number.isFinite(price) && price > 0) parts.push(`$${Math.round(price).toLocaleString('en-US')}`)
  if (o.lead_side && SIDE_WORD[o.lead_side]) parts.push(SIDE_WORD[o.lead_side])
  if (o.lead_source_label) parts.push(o.lead_source_label)
  return parts.join(' · ')
}

/** The lead's name as an offer shows it ("A lead" once it was removed). */
export function offerLeadName(o: Pick<LeadOffer, 'lead_name'>): string {
  return o.lead_name ?? 'A lead'
}

/** Where the bell and the email send a member for one offer. */
export function offerHref(offerId: string): string {
  return `/contacts/claim?offer=${encodeURIComponent(offerId)}`
}

/** The offer a bell row is about, if it is one (event lead.offer, data.offer_id). */
export function offerIdOfNotification(n: { event_type?: string | null; data?: unknown }): string | null {
  if (n.event_type !== 'lead.offer') return null
  const d = n.data
  if (!d || typeof d !== 'object') return null
  return str((d as Record<string, unknown>).offer_id)
}

/** Open offers first (soonest to close first), then the rest newest first. */
export function sortOffers(offers: readonly LeadOffer[]): LeadOffer[] {
  return [...offers].sort((a, b) => {
    if (a.can_claim !== b.can_claim) return a.can_claim ? -1 : 1
    if (a.can_claim) return Date.parse(a.expires_at) - Date.parse(b.expires_at)
    return Date.parse(b.offered_at) - Date.parse(a.offered_at)
  })
}
